/**
 * PROMO SCREEN — the only thing a visitor sees when a card's promo is On.
 *
 * The card's own look (its logo and wallpaper), a headline, a short message
 * and one Continue button. No review question, no automatic redirect, no
 * counter, nothing about the visitor in the link.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = `${(typeof window !== 'undefined' ? window.location.origin : '')}/api`;

// The SAME logo-size setting and the SAME values Review Shield uses.
// No size set → today's size (h-20 w-auto).
const LOGO_SIZE_CLASSES = {
    small: 'max-h-16 max-w-[120px]',
    medium: 'max-h-20 max-w-[180px]',
    large: 'max-h-32 max-w-[280px]',
    very_large: 'max-h-44 max-w-[360px]',
};

export default function PromoScreen() {
    const { cardId } = useParams();
    const { i18n, t } = useTranslation();
    const [data, setData] = useState(null);
    const [state, setState] = useState('loading');

    // The visitor's own language, read exactly as the other public screens do.
    const lang = useMemo(() => {
        let stored = null;
        try { stored = localStorage.getItem('yulontap_lang'); } catch (e) { stored = null; }
        if (stored) {
            if (stored !== i18n.language) i18n.changeLanguage(stored);
            return stored.slice(0, 2);
        }
        return (i18n.language || 'en').slice(0, 2);
    }, [i18n]);

    useEffect(() => {
        let alive = true;
        axios.get(`${API}/public/promo/${cardId}`, { params: { lang } })
            .then((r) => { if (alive) { setData(r.data); setState('ready'); } })
            .catch(() => {
                // No promo any more (Off, Draft, past its dates, deleted): send
                // the visitor straight back into the card's normal flow.
                if (alive) {
                    setState('gone');
                    window.location.replace(`${API}/tap?card=${encodeURIComponent(cardId)}`);
                }
            });
        return () => { alive = false; };
    }, [cardId, lang]);

    if (state === 'loading' || state === 'gone') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-950"
                 data-testid="promo-screen-loading">
                <div className="h-8 w-8 rounded-full border-2 border-white/20 border-t-white/80 animate-spin" />
            </div>
        );
    }

    const go = () => {
        if (!data.link) return;
        if (data.open_in === 'new') {
            window.open(data.link, '_blank', 'noopener,noreferrer');
        } else {
            window.location.href = data.link;
        }
    };

    // Optional second action button. Absent = the screen is exactly as before.
    const second = data.second_button;
    const secondButton = second ? (
        <a href={second.url} target="_blank" rel="noopener noreferrer"
           data-testid="promo-screen-second-button"
           className="block w-full rounded-full px-6 py-4 text-lg font-semibold text-center
                      transition-transform duration-150 active:scale-[0.98]"
           style={{ background: second.color, color: data.text_color }}>
            {second.text}
        </a>
    ) : null;

    return (
        <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12 bg-slate-950"
             style={data.background_url ? {
                 backgroundImage: `linear-gradient(rgba(2,6,23,.72), rgba(2,6,23,.88)), url(${data.background_url})`,
                 backgroundSize: 'cover', backgroundPosition: 'center',
             } : undefined}
             data-testid="promo-screen">
            <div className="w-full max-w-md text-center space-y-7">
                {data.logo_url && (
                    <img src={data.logo_url} alt={data.organization_name || ''}
                         className={`mx-auto object-contain ${LOGO_SIZE_CLASSES[data.logo_size] || 'h-20 w-auto'}`}
                         data-testid="promo-screen-logo" />
                )}
                <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight"
                    data-testid="promo-screen-headline">
                    {data.headline}
                </h1>
                {data.message && (
                    <p className="text-base text-white/80 leading-relaxed"
                       data-testid="promo-screen-message">
                        {data.message}
                    </p>
                )}
                {second && second.position === 'above' && secondButton}
                <button type="button" onClick={go} data-testid="promo-screen-continue"
                        className="w-full rounded-full px-6 py-4 text-lg font-semibold
                                   transition-transform duration-150 active:scale-[0.98]"
                        style={{ background: data.bg_color, color: data.text_color }}>
                    {data.continue_text || t('promo.continue', 'Continue')}
                </button>
                {second && second.position !== 'above' && secondButton}
            </div>
        </div>
    );
}
